import { Clock, LinearFilter, PerspectiveCamera, Scene, WebGLRenderer, PlaneBufferGeometry } from 'three';

import Home from './home';
import About from './about';
import CaseStudy from './case-study';
import News from './news';
import SignUp from './sign-up';
import Background from './background';

export default class Canvas {

	isPointer = false;

	clock;
	data;
	geometry;

	currentBackground;
	currentPage;

	slug;

	scroll = null;

	heroes = [];

	modules = [];

	constructor({ slug }) {
		this.clock = new Clock();

		this.data = APPDATA.webgl;

		this.slug = slug;

		this.textures = [];

		this.sizes = {
			environment: {
				height: 0,
				width: 0
			},
			retina: Math.min(window.devicePixelRatio, 2),
			screen: {
				height: window.innerHeight,
				width: window.innerWidth
			}
		};

		this.geometry = new PlaneBufferGeometry(
			1,
			1,
			80,
			80
		);

		this.createRenderer();
		this.createScene();
		this.createBackground();

		this.resize();
		this.update();
	}

	createBackground() {
		this.currentBackground = new Background({
			scene: this.scene,
			clock: this.clock,
			sizes: this.sizes,
			geometry: this.geometry
		});

		this.currentBackground.init();
	}

	createRenderer() {
		this.renderer = new WebGLRenderer({
			alpha: true,
			antialias: false,
			powerPreference: 'high-performance'
		});

		this.renderer.setPixelRatio(this.sizes.retina);
		this.renderer.setSize(this.sizes.screen.width, this.sizes.screen.height);
		this.renderer.domElement.className = 'canvas';

		document.body.appendChild(this.renderer.domElement);
	}

	createScene() {
		this.scene = new Scene();

		this.camera = new PerspectiveCamera(
			70,
			this.sizes.screen.width / this.sizes.screen.height,
			300,
			1000
		);

		this.cameraDistance = 400;
		this.camera.position.set(0, 0, this.cameraDistance);
		this.camera.lookAt(0, 0, 0);
	}

	loadModules(data) {
		const module = {
			...data,
			textures: []
		};

		for (const texture of data.textures) {
			texture.generateMipmaps = false;
			texture.minFilter = LinearFilter;
			texture.needsUpdate = true;
			this.renderer.initTexture(texture, 0);
			module.textures.push(texture);
		}

		this.modules.push(module);
	}

	complete() {
		this.show(this.slug, true);
	}

	hide(isTop) {
		return new Promise(async resolve => {
			if (this.currentPage) {
				await this.currentPage.hide(isTop);
				this.currentPage = null;
				resolve();
			} else {
				resolve();
			}
		});
	}

	show(currentSlug, isPreload) {
		if (currentSlug === 'home') {
			const modules = this.modules.filter(match => match.slug === currentSlug);
			this.currentPage = new Home({
				modules,
				scene: this.scene,
				clock: this.clock,
				sizes: this.sizes,
				geometry: this.geometry,
				isPreload
			});
		}

		if (currentSlug === 'about') {
			const modules = this.modules.filter(match => match.slug === currentSlug);
			this.currentPage = new About({
				modules,
				scene: this.scene,
				clock: this.clock,
				sizes: this.sizes,
				geometry: this.geometry,
				isPreload
			});
		}

		if (currentSlug === 'news') {
			const modules = this.modules.filter(match => match.slug === currentSlug);
			this.currentPage = new News({
				modules,
				scene: this.scene,
				clock: this.clock,
				sizes: this.sizes,
				geometry: this.geometry,
				isPreload
			});
		}

		if (currentSlug === 'sign-up') {
			const modules = this.modules.filter(match => match.slug === currentSlug);
			this.currentPage = new SignUp({
				modules,
				scene: this.scene,
				clock: this.clock,
				sizes: this.sizes,
				geometry: this.geometry,
				renderer: this.renderer,
				isPreload
			});
		}

		if (currentSlug.includes('case-studies')) {
			this.currentPage = new CaseStudy({
				scene: this.scene,
				clock: this.clock,
				sizes: this.sizes,
				geometry: this.geometry,
				renderer: this.renderer
			});
		}

		if (currentSlug.includes('case-studies/') || currentSlug.includes('news/')) {
			this.currentBackground.setColor('tan');
		} else {
			this.currentBackground.setColor();
		}

		if (this.currentPage) {
			this.currentPage.init();
			if (!currentSlug.includes('case-studies')) {
				this.currentPage.show();
			}
		}
	}

	/**
	 * Resize
	 */
	resize() {
		this.sizes.screen = {
			height: window.innerHeight,
			width: window.innerWidth
		};

		this.renderer.setSize(
			this.sizes.screen.width,
			this.sizes.screen.height
		);

		this.camera.aspect = this.sizes.screen.width / this.sizes.screen.height;
		this.camera.fov = 2 * Math.atan(this.sizes.screen.width / this.camera.aspect / (2 * this.cameraDistance)) * (180 / Math.PI);
		this.camera.updateProjectionMatrix();

		if (this.currentPage) {
			this.currentPage.resize({
				sizes: this.sizes
			});
		}

		if (this.currentBackground) {
			this.currentBackground.resize({
				sizes: this.sizes
			});
		}
	}

	/**
	 * Update.
	 */
	update(scroll) {
		if (this.scroll !== scroll) {
			this.scroll = scroll;
			if (this.currentPage && this.currentPage.update) {
				this.currentPage.update(scroll);
			}
		}

		this.renderer.render(this.scene, this.camera);
	}

}
